 
import React from "react";
import { ResponsiveCommandSelect } from "@/components/ui/responsive-select";
import type { SimpleFieldConfig } from "../types";
import type { FilterState } from "@/components/shared/types";
import { toCommandOptions, resolveDatetimePresetSelections } from "./filter-rendering-helpers";

interface DatetimePreset {
  value: string;
  label: string;
}

interface DatetimeFilterProps {
  fieldId: string;
  fieldConfig: SimpleFieldConfig;
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
  getDatetimePresets: () => DatetimePreset[];
  handleCustomRangeClick?: (fieldId: string, label: string) => void;
}

export function DatetimePresetFilter({
  fieldId,
  fieldConfig,
  filters,
  onFilterChange,
  getDatetimePresets,
  handleCustomRangeClick,
}: DatetimeFilterProps) {
  const presets = getDatetimePresets();
  const enabledPresets = resolveDatetimePresetSelections(presets, fieldConfig.datetimePresets?.presets);

  return (
    <div key={fieldId} className="w-full overflow-visible">
      <ResponsiveCommandSelect
        value={(filters[fieldId] as string | undefined) || "all"}
        onValueChange={(value) => {
          const newFilters = { ...filters };
          if (value === "all") {
            delete newFilters[fieldId];
          } else if (value === "custom") {
            handleCustomRangeClick?.(fieldId, fieldConfig.label);
            return; // Wait for modal before updating filters
          } else {
            newFilters[fieldId] = value;
          }
          onFilterChange(newFilters);
        }}
        placeholder={fieldConfig.label}
        className="w-full"
        highZIndex={true}
        expandInline={true}
        expandInlineVariant="replace"
        options={toCommandOptions([
          { value: "all", label: `All ${fieldConfig.label}` },
          ...enabledPresets.map((preset) => ({ value: preset.value, label: preset.label })),
          ...(fieldConfig.datetimePresets?.showCustomRange
            ? [{ value: "custom", label: "Custom range..." }]
            : []),
        ])}
      />
    </div>
  );
}
